// Centralized data store for dog profiles
//
// Photo framing is per context, because each one crops the same photo into a
// different shape:
//   availablePuppies_backgroundPosition  parent cards on the puppies page
//   ourGators_backgroundPosition         cards in the Our Gators grid
//   dogPage_objectPosition               the photo on the dog's own page
//
// Each of those takes an optional mobile counterpart, used below 768px where
// the cards stack and change shape:
//   availablePuppies_mobile_backgroundPosition
//   ourGators_mobile_backgroundPosition
//   dogPage_mobile_objectPosition
//
// Leave a mobile one out and it follows the desktop value, so only add one
// where the phone crop actually needs to differ.
export const dogProfiles = {
  dume: {
    id: 'dume',
    name: 'Dume',
    status: 'Active Stud',
    image: '/dog-photos/dume/profile.jpg',
    availablePuppies_backgroundPosition: 'center 40%',
    ourGators_backgroundPosition: 'center 40%',
    dogPage_objectPosition: 'center 40%',
    short_bio: 'Dume is our stunning Lilac Platinum French Bulldog with exceptional size and structure, set to be the cornerstone of our breeding program.',
    long_bio: "Dume is a remarkable Lilac Platinum French Bulldog who embodies the ideal traits of the breed. With his outstanding size and structure, he is poised to be the cornerstone of our breeding program. Dume's striking appearance, combined with his balanced proportions and strong conformation, makes him a standout in every way. Beyond his physical attributes, Dume's gentle and playful nature ensures he is a joy to be around. As a fluffy carrier, Dume brings an added layer of genetic diversity and potential to our program. As we look to the future, Dume's exceptional qualities will play a pivotal role in shaping the next generation of our French Bulldogs, passing on his superior genetics and charm.",
    color: 'Lilac Platinum',
    weight: '25 lbs',
    dna: 'At/At d/d co/co e/e I/I L/l',
    temperament: 'Playful, Gentle, Sociable',
    path: '/our-gators/dume',
    achievements: ['Show Quality', 'Health Tested', 'Excellent Temperament'],
    gallery: '/dog-photos/dume',
    visible: true
  },
  hotsauce: {
    id: 'hotsauce',
    name: 'Hot Sauce',
    analyticsName: 'HotSauce',
    status: 'Stud',
    image: '/dog-photos/hotsauce/profile.jpg',
    availablePuppies_backgroundPosition: 'center +43%',
    ourGators_backgroundPosition: 'center +35%',
    dogPage_objectPosition: 'center +35%',
    short_bio: 'Hot Sauce is our affectionate and active Isabella French Bulldog with exceptional structure. This sweet boy loves attention and brings joy to everyone he meets with his loving personality.',
    long_bio: "Hot Sauce is an exceptional Isabella French Bulldog who embodies the perfect combination of beauty, structure, and personality. This affectionate and active boy has captured our hearts with his sweet nature and love for attention. Hot Sauce thrives on human interaction and has an incredible ability to make everyone feel special with his loving demeanor. His outstanding structure showcases the ideal French Bulldog conformation, making him not only a wonderful companion but also a valuable addition to our breeding program. As an Isabella carrying one copy of the coco gene, Hot Sauce brings excellent genetic diversity and the potential to produce stunning coat variations in his offspring. His active lifestyle and playful spirit, combined with his gentle and affectionate temperament, make him a joy to be around and ensure he passes on his exceptional qualities to the next generation.",
    color: 'Isabella Merle',
    weight: '23 lbs',
    dna: 'At/At d/d Co/co b/b E/e I/I n/S M/m',
    temperament: 'Calm, Intelligent, Friendly',
    path: '/our-gators/hotsauce',
    achievements: ['Grand Champion Bloodline', 'Proven Stud', 'Health Tested'],
    gallery: '/dog-photos/hotsauce',
    visible: true
  },
  revan: {
    id: 'revan',
    name: 'Revan',
    status: 'Retired Stud',
    image: '/dog-photos/revan/profile.png',
    availablePuppies_backgroundPosition: 'center +14%',
    ourGators_backgroundPosition: 'center +14%',
    dogPage_objectPosition: 'center +14%',
    short_bio: 'Revan is our happy-go-lucky French Bulldog with a great smile and a sweet, intelligent personality. His willingness to please and excellent temperament make him a joy to be around.',
    long_bio: "Revan is the embodiment of joy and intelligence in a French Bulldog. Known for his great smile and happy-go-lucky attitude, he brings positivity wherever he goes. Revan's sweet nature and high intelligence make him a quick learner who trains exceptionally well. His willingness to please and calm demeanor make him a favorite among people and other dogs alike. With his outstanding temperament and friendly disposition, Revan is not only a wonderful companion but also a great father to his puppies. He consistently passes on his exceptional traits, ensuring his legacy of excellence continues.",
    color: 'Blue Trindle',
    weight: '29 lbs',
    dna: 'At/At d/d Co/co Kbr/Ky E/e N/S',
    temperament: 'Calm, Intelligent, Friendly',
    path: '/our-gators/revan',
    achievements: ['Grand Champion Bloodline', 'Proven Stud', 'Health Tested'],
    gallery: '/dog-photos/revan',
    visible: false
  },
  pearl: {
    id: 'pearl',
    name: 'Pearl',
    status: 'Retired',
    image: '/dog-photos/pearl/profile.jpg',
    availablePuppies_backgroundPosition: 'center +42%',
    ourGators_backgroundPosition: 'center +42%',
    dogPage_objectPosition: 'center +42%',
    short_bio: 'Pearl is our stunning blue and tan French Bulldog, known for her excellent mothering skills, goofy personality, and her ability to produce large, healthy litters. She is also a fluffy carrier.',
    long_bio: "Pearl is a beautiful blue and tan French Bulldog who brings elegance, charm, and a touch of goofiness to our breeding program. Her striking coat colors and perfect structure make her a standout, but it is her exceptional qualities as a mother that truly set her apart. Pearl has consistently produced 8+ puppy litters, ensuring her legacy of healthy and well-adjusted puppies. Her sweet and nurturing nature, combined with her goofy and gentle personality, make her an invaluable part of our breeding family. We are proud to have her as a cornerstone of our program.",
    color: 'Blue and Tan',
    weight: '26 lbs',
    dna: 'At/At d/d Co/co Em/e L/l',
    temperament: 'Sweet, Nurturing, Gentle',
    path: '/our-gators/pearl',
    achievements: ['Champion Bloodline', 'Excellent Mother', 'Health Tested'],
    gallery: '/dog-photos/pearl',
    visible: false
  },
  tonks: {
    id: 'tonks',
    name: 'Tonks',
    status: 'Active Breeding',
    image: '/dog-photos/tonks/profile.jpg',
    availablePuppies_backgroundPosition: 'center +18%',
    ourGators_backgroundPosition: 'center +18%',
    dogPage_objectPosition: 'center 0%',
    short_bio: 'Tonks is a blue and tan merle who carries cocoa, testable chocolate, and fluffy, giving her the range to produce blue, lilac, Isabella, and new shade Isabella puppies in either coat.',
    long_bio: "Tonks is a blue and tan merle whose genetics make her one of the most versatile females in our program. Carrying cocoa, testable chocolate, and fluffy, she brings together everything needed to produce blue, lilac, Isabella, and new shade Isabella puppies in both the standard and long coat, and as a merle herself she passes that patterning on as well. Now fully grown, Tonks has kept every bit of the energy she had as a puppy and added a deep attachment to her people on top of it. She follows whoever she has claimed from room to room, holds up her end of any conversation with the grumbles and chatter the breed is known for, and remains completely convinced that she is a lap dog no matter what the scale says. Her genetics and her personality together make her a valuable part of our program.",
    color: 'Blue and Tan Merle',
    weight: '24 lbs',
    dna: 'At/At d/d Co/co B/b Em/Em M/m L/l',
    temperament: 'Energetic, Affectionate, Smart',
    path: '/our-gators/tonks',
    achievements: ['Health Tested', 'Outstanding Temperament'],
    gallery: '/dog-photos/tonks',
    visible: true
  },
  gidget: {
    id: 'gidget',
    name: 'Gidget',
    status: 'Active Breeding',
    image: '/dog-photos/gidget/profile.jpg',
    availablePuppies_backgroundPosition: 'center +42%',
    ourGators_backgroundPosition: 'center +45%',
    dogPage_objectPosition: 'center +45%',
    short_bio: 'Gidget is our stunning sable French Bulldog who carries blue, cocoa, and testable chocolate. As a 2x Hamato Yoshi granddaughter, she boasts an excellent build and brings a sassy, spunky, and affectionate personality to our program.',
    long_bio: "Gidget is a remarkable sable French Bulldog who brings exceptional genetics and personality to our breeding program. As a 2x Hamato Yoshi granddaughter, she inherited an excellent build and superior conformation that makes her stand out in every way. Carrying blue, cocoa, and testable chocolate, Gidget offers incredible genetic diversity and the potential to produce a stunning variety of rare coat colors in her offspring. Her sassy and spunky personality is perfectly balanced with her affectionate nature, making her both entertaining and deeply loving. Gidget's combination of outstanding lineage, valuable genetics, and dynamic temperament make her an invaluable asset to our program and a joy to have in our lives.",
    color: 'Sable',
    weight: '22 lbs',
    dna: 'At/Ay D/d Co/co, B/b, Em/Em n/S',
    temperament: 'Sassy, Spunky, Affectionate',
    path: '/our-gators/gidget',
    achievements: ['Champion Bloodline', 'Hamato Yoshi Lineage', 'Health Tested'],
    gallery: '/dog-photos/gidget',
    visible: false
  },
  zannah: {
    id: 'zannah',
    name: 'Zannah',
    status: 'Active Breeding',
    image: '/dog-photos/zannah/profile.jpg',
    availablePuppies_backgroundPosition: 'center +37%',
    ourGators_backgroundPosition: 'center +35%',
    dogPage_objectPosition: 'center +15%',
    short_bio: 'Zannah is our adorable and sweet brindle merle French Bulldog. Her small size, excellent body structure, and loving personality make her a standout in our program.',
    long_bio: "Zannah is the epitome of charm and affection in a French Bulldog. Her petite size and great body structure make her a beautiful representation of the breed. Zannah's love for people is unmatched; she thrives on human interaction and brings joy to everyone she meets. As a mother, she is exceptional, nurturing her puppies with care and dedication. Her gentle and sweet nature, combined with her strong maternal instincts, ensures that her puppies are healthy, happy, and well-adjusted. Zannah's presence in our breeding program is a testament to her outstanding qualities as both a companion and a mother.",
    color: 'Brindle Merle',
    weight: '23 lbs',
    dna: 'Ay/Ay Co/co Kbr/Ky Em/e N/S M/m',
    temperament: 'Calm, Confident, Affectionate',
    path: '/our-gators/zannah',
    achievements: ['Champion Bloodline', 'Health Tested'],
    gallery: '/dog-photos/zannah',
    visible: true
  }
};
